import {
  AuthService,
  ConfigStateService,
  NAVIGATE_TO_MANAGE_PROFILE,
  SessionStateService,
  RoutesService,
  PermissionService,
  eLayoutType,
} from '@abp/ng.core';
import {
  Component,
  OnInit,
  TrackByFunction,
  ViewEncapsulation,
  inject,
  HostListener,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable } from 'rxjs';
import { map, filter, startWith } from 'rxjs/operators';
import { eThemeBasicComponents } from '../../models/components';
import { UserMenu, UserMenuService } from '@abp/ng.theme.shared';
// Local notification interfaces
interface DynamicNotification {
  id: string;
  title: string;
  description: string;
  url?: string;
  type: 'info' | 'success' | 'warning' | 'danger' | 'profile';
  read: boolean;
  timestamp: Date;
  time: string;
}

@Component({
  standalone: false,
  selector: 'abp-layout-application',
  templateUrl: './application-layout.component.html',
  styleUrls: ['./application-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ApplicationLayoutComponent implements OnInit {
  private readonly sessionState = inject(SessionStateService);
  private readonly authService = inject(AuthService);
  private readonly navigateToManageProfile = inject(NAVIGATE_TO_MANAGE_PROFILE);
  private readonly routesService = inject(RoutesService);
  private readonly userMenuService = inject(UserMenuService);
  private readonly configState = inject(ConfigStateService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  isCollapsed = true;
  isSidebarCollapsed = false;
  isMobileMenuOpen = false;
  isUserMenuOpen = false;
  isNotificationDropdownOpen = false;

  // Dynamic notifications
  dynamicNotifications: DynamicNotification[] = [];
  private nextNotificationId = 1;

  get unreadCount(): number {
    return this.dynamicNotifications.filter(n => !n.read).length;
  }

  get hasUnreadNotifications(): boolean {
    return this.unreadCount > 0;
  }

  get currentUser$(): Observable<any> {
    return this.configState.getOne$('currentUser');
  }

  get currentUserImage$(): Observable<string | null> {
    return this.currentUser$.pipe(
      map((user): string | null => {
        if (user?.isAuthenticated) {
          // Handle avatar from user properties or other sources
          return null;
        }
        return null;
      })
    );
  }

  get smallScreen(): boolean {
    return window.innerWidth < 992;
  }

  get currentUser() {
    return this.configState.getOne('currentUser');
  }

  get appInfo(): { name: string; logoUrl: string | null } {
    const config = this.configState.getAll();
    const defaultResourceName = config?.localization?.defaultResourceName;
    return {
      name: defaultResourceName || 'MyApp',
      logoUrl: null as string | null
    };
  }

  visibleRoutes$: Observable<any[]>;
  navItems$: Observable<any[]>;
  userMenuItems$: Observable<UserMenu[]>;
  pageTitle$: Observable<string>;

  trackByFn: TrackByFunction<any> = (_, element) => element.name || element.id;

  constructor() {
    this.navItems$ = this.routesService.visible$;
    this.userMenuItems$ = this.userMenuService.items$;

    // Create visibleRoutes$ only once
    this.visibleRoutes$ = this.navItems$.pipe(
      map(items => {
        // Filter for application layout routes only
        return items.filter(item =>
          item.layout === eLayoutType.application &&
          (item.path || (item.name && !item.invisible))
        );
      })
    );

    // Create dynamic page title based on current route
    this.pageTitle$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null), // Trigger immediately on init
      map(() => {
        const currentPath = this.router.url.split('?')[0]; // Remove query params
        const routes = this.routesService.flat;

        // Find the route that matches the current path
        const matchedRoute = routes.find(route => {
          if (!route.path) return false;
          // Exact match or starts with the route path
          return currentPath === route.path ||
                 (currentPath.startsWith(route.path) && route.path !== '/');
        });

        return matchedRoute?.name || 'Dashboard';
      })
    );
  }

  ngOnInit() {
    // Component initialization
    this.checkProfileCompletion();
    this.initializeDemoNotifications();
  }

  onLogout() {
    this.authService.logout().subscribe();
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  navigateToManageProfilePage() {
    this.navigateToManageProfile();
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const userMenu = target.closest('.user-menu');
    const notifications = target.closest('.notifications');
    
    if (!userMenu && this.isUserMenuOpen) {
      this.isUserMenuOpen = false;
    }
    
    if (!notifications && this.isNotificationDropdownOpen) {
      this.isNotificationDropdownOpen = false;
    }
  }

  isDropdownChildActive(children: any[]): boolean {
    return children.some(child => window.location.pathname.startsWith(child.path));
  }

  openChange(event: boolean, childrenContainer: HTMLDivElement) {
    if (!event) {
      childrenContainer
        .querySelector('.navigation-backdrop')
        ?.removeEventListener('click', this.clearCollapsed);
    }
  }

  clearCollapsed = () => {
    this.isCollapsed = true;
  };

  checkProfileCompletion(): void {
    // Simulate checking for missing coach profile fields
    // In real implementation, you'd get the current user and their profile from the backend

    // Mock check - assume user is a coach with missing profile info for demo
    const isCoach = true; // Would come from auth service

    if (isCoach) {
      // Mock check - in reality you'd get this from the coach profile API
      const hasPhone = false; // Would come from coach profile
      const hasExpertiseAreas = false; // Would come from coach profile
      const hasExperience = false; // Would come from coach profile

      const missingFields: string[] = [];
      if (!hasPhone) {
        missingFields.push('Telefon numarası');
      }
      if (!hasExpertiseAreas) {
        missingFields.push('Uzmanlık alanları');
      }
      if (!hasExperience) {
        missingFields.push('Deneyim bilgileri');
      }

      // Add profile completion notification to the dynamic system
      if (missingFields.length > 0) {
        this.addDynamicNotification(
          'Profil Bilgilerinizi Tamamlayın',
          `Lütfen şu alanları tamamlayın: ${missingFields.join(', ')}`,
          '/coach/profile-completion',
          'profile'
        );
      }
    }
  }

  initializeDemoNotifications(): void {
    // Add some demo notifications to showcase the dynamic system
    this.addDynamicNotification(
      'Welcome to Sudocad.io',
      'Your coaching platform is ready to use!',
      '/dashboard',
      'success'
    );

    this.addDynamicNotification(
      'New Student Request',
      'Ahmet Y. has requested you as their coach.',
      '/coach/my-students',
      'info'
    );

    this.addDynamicNotification(
      'System Maintenance',
      'Scheduled maintenance will occur tomorrow at 2 AM.',
      '/dashboard',
      'warning'
    );
  }

  addDynamicNotification(
    title: string,
    description: string,
    url?: string,
    type: DynamicNotification['type'] = 'info'
  ): void {
    const notification: DynamicNotification = {
      id: `notification_${this.nextNotificationId++}`,
      title,
      description,
      url,
      type,
      read: false,
      timestamp: new Date(),
      time: this.formatTimeAgo(new Date())
    };

    this.dynamicNotifications.unshift(notification);
  }

  markAllAsRead(): void {
    this.dynamicNotifications.forEach(notification => {
      notification.read = true;
    });
  }

  markAsRead(notificationId: string): void {
    const notification = this.dynamicNotifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
    }
  }

  removeNotification(notificationId: string): void {
    this.dynamicNotifications = this.dynamicNotifications.filter(n => n.id !== notificationId);
  }

  navigateToNotification(notification: DynamicNotification): void {
    if (notification.url) {
      console.log('Navigating to:', notification.url);
      this.router.navigate([notification.url])
        .then((success) => {
          console.log('Navigation result:', success);
          if (success) {
            this.markAsRead(notification.id);
            this.isNotificationDropdownOpen = false;
          }
        })
        .catch((error) => {
          console.error('Navigation error:', error);
        });
    } else {
      console.log('No URL provided for notification:', notification);
    }
  }

  getNotificationIcon(type: DynamicNotification['type']): string {
    const icons = {
      'info': 'fas fa-info-circle',
      'success': 'fas fa-check-circle',
      'warning': 'fas fa-exclamation-triangle',
      'danger': 'fas fa-times-circle',
      'profile': 'fas fa-user'
    };
    return icons[type] || icons['info'];
  }

  getNotificationColor(type: DynamicNotification['type']): string {
    const colors = {
      'info': 'bg-primary',
      'success': 'bg-success',
      'warning': 'bg-warning',
      'danger': 'bg-danger',
      'profile': 'bg-secondary'
    };
    return colors[type] || colors['info'];
  }

  private formatTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  }

  toggleNotificationDropdown(): void {
    this.isNotificationDropdownOpen = !this.isNotificationDropdownOpen;
  }
}